
import React, { useMemo, useLayoutEffect, useRef } from 'react'
import { Position, useNodeConnections, useStore, useNodesData, useUpdateNodeInternals, NodeResizer, type Node } from '@xyflow/react'
import { GenieHandle } from './GenieHandle'
import { getHandleIcon } from '@/lib/canvas/configs/handle-icons'
import { isDataTypeCompatible } from '@/components/canvas/utils/connection-validator'
import { resolveHandlesForNode } from '@/lib/canvas/configs/handles'
import { getNodeHandleLayout, NODE_LAYOUT, NODE_MIN_HEIGHT } from '@genieartinc/schemas'
import type { HandleMeta } from '@/lib/canvas/types'

/** Stable empty array to avoid recreating useNodesData selector on every render */
const EMPTY_NODE_IDS: string[] = []

/**
 * NodeHandles - Renders GenieHandle components from HandleMeta[]
 *
 * Reads handle metadata and renders styled, icon-bearing handles
 * with proper positioning, connection state, and tooltips.
 */

interface NodeHandlesProps {
  nodeId: string
  handles: HandleMeta[]
}

function NodeHandlesComponent({ nodeId, handles }: NodeHandlesProps) {
  const connections = useNodeConnections({ id: nodeId })
  const selected = useStore((store) => Boolean(store.nodeLookup.get(nodeId)?.selected))

  const root = useRef<HTMLDivElement>(null)
  const updateNodeInternals = useUpdateNodeInternals()
  const layout = useMemo(() => getNodeHandleLayout(handles), [handles])

  useLayoutEffect(() => {
    const node = root.current?.closest<HTMLElement>('.react-flow__node')
    if (!node) return
    node.style.setProperty('--node-port-min-height', `${layout.minHeight}px`)
    node.style.setProperty('--node-port-min-width', `${layout.minWidth}px`)
    // Both the RF hitbox and the card must grow when horizontal ports need room.
    node.style.minWidth = `max(${NODE_LAYOUT.minWidth}px, var(--node-port-min-width, 0px))`
    // RF observes card size changes; this also refreshes edges when ports change
    // order/count without changing the node's overall dimensions.
    updateNodeInternals(nodeId)
    return () => {
      node.style.removeProperty('--node-port-min-height')
      node.style.removeProperty('--node-port-min-width')
      node.style.removeProperty('min-width')
    }
  }, [nodeId, layout, updateNodeInternals])

  // Detect active connection drag from React Flow store
  const connectionStartHandle = useStore((s) => s.connection.fromHandle)

  // Only subscribe to the dragging source node's data (not ALL nodes)
  const sourceNodeId = connectionStartHandle?.nodeId ?? null
  const sourceNodesData = useNodesData(sourceNodeId ? [sourceNodeId] : EMPTY_NODE_IDS)

  // Resolve the dragging handle's dataType and type direction
  const dragInfo = useMemo(() => {
    if (!connectionStartHandle) return null
    const sourceHandleId = connectionStartHandle.id
    const sourceHandleType = connectionStartHandle.type // 'source' (output) or 'target' (input)
    if (!sourceNodeId || !sourceHandleId) return null

    // Read handle metadata from the source node (with fallback resolution)
    const sourceData = sourceNodesData[0]
    if (!sourceData) return null
    const sourceHandles = resolveHandlesForNode(sourceData as unknown as Node)
    const sourceMeta = sourceHandles.find((h) => h.id === sourceHandleId)
    const dataType = sourceMeta?.dataType || 'any'

    return { dataType, handleType: sourceHandleType, sourceNodeId }
  }, [connectionStartHandle, sourceNodeId, sourceNodesData])

  // Determine which handles are connected
  const connectedHandleIds = useMemo(() => {
    const ids = new Set<string>()
    connections.forEach((conn) => {
      if (conn.source === nodeId && conn.sourceHandle) {
        ids.add(conn.sourceHandle)
      }
      if (conn.target === nodeId && conn.targetHandle) {
        ids.add(conn.targetHandle)
      }
      // Handle case where no handle ID is specified (generic connection)
      if (conn.source === nodeId && !conn.sourceHandle) {
        const firstOutput = handles.find(h => h.type === 'output')
        if (firstOutput) ids.add(firstOutput.id)
      }
      if (conn.target === nodeId && !conn.targetHandle) {
        const firstInput = handles.find(h => h.type === 'input')
        if (firstInput) ids.add(firstInput.id)
      }
    })
    return ids
  }, [connections, nodeId, handles])

  return (
    <div ref={root} style={{ display: 'contents' }}>
      <NodeResizer
        nodeId={nodeId}
        isVisible={selected}
        minWidth={Math.max(NODE_LAYOUT.minWidth, layout.minWidth)}
        minHeight={Math.max(NODE_MIN_HEIGHT, layout.minHeight)}
        keepAspectRatio={false}
        autoScale
        lineClassName="genie-node-resize-line"
        handleClassName="genie-node-resize-handle"
      />
      {layout.placements.map(({ handle, side, offset }) => {
        const rfPosition = side as Position
        const isConnected = connectedHandleIds.has(handle.id)
        const isSmallMode = handle.mode === 'SMALL'

        // Resolve icon
        const iconName = handle.metadata?.icon
          || getHandleIcon(handle.metadata?.parameterName || handle.id, handle.dataType)

        // Build label
        const label = handle.label
          || handle.metadata?.parameterLabel
          || handle.id

        const horizontal = side === Position.Top || side === Position.Bottom
        const posStyle: React.CSSProperties = horizontal ? { left: `${offset}px` } : { top: `${offset}px` }

        // Compatibility opacity during drag
        let compatibilityOpacity: number | undefined
        if (dragInfo) {
          // Dragging from output (source) → only target (input) handles are valid drop targets, and vice versa
          const rfHandleType = handle.type === 'output' ? 'source' : 'target'
          const isSameNode = dragInfo.sourceNodeId === nodeId
          const isOppositeType = rfHandleType !== dragInfo.handleType
          const handleDataType = handle.dataType || 'any'

          if (isSameNode || !isOppositeType) {
            // Same node or same direction (output→output / input→input) — incompatible
            compatibilityOpacity = 0.3
          } else if (handle.locked && handle.type === 'input') {
            // Locked input handles don't accept connections
            compatibilityOpacity = 0.3
          } else if (dragInfo.handleType === 'source') {
            // Dragging from output → check source→target compatibility
            compatibilityOpacity = isDataTypeCompatible(dragInfo.dataType, handleDataType) ? 1 : 0.3
          } else {
            // Dragging from input → check target→source compatibility
            compatibilityOpacity = isDataTypeCompatible(handleDataType, dragInfo.dataType) ? 1 : 0.3
          }
        }

        return (
          <GenieHandle
            key={handle.id}
            id={handle.id}
            type={handle.type === 'output' ? 'source' : 'target'}
            position={rfPosition}
            isConnected={isConnected}
            compatibilityOpacity={compatibilityOpacity}
            icon={iconName}
            compact={isSmallMode}
            label={label}
            style={posStyle}
          />
        )
      })}
    </div>
  )
}

export const NodeHandles = React.memo(NodeHandlesComponent)
NodeHandles.displayName = 'NodeHandles'
